import type { ComputedRef, Ref, ShallowRef } from 'vue';

import { useWindowScroll, watchThrottled } from '@vueuse/core';
import { useScrollDirection } from '~/composables/useScrollDirection';
import { DependencyInjectionSymbols } from '~/core/helpers/symbols';
import { useNavigationStore } from '~/store/navigationStore';
import { usePageSectionsStore } from '~/store/pageSectionStore';
import { computed, inject, ref, unref, watch, watchPostEffect } from 'vue';

type HeaderVariant = 'primary' | 'translucide';

type ScrollDirection = 'up' | 'down' | null;

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

interface HeaderStateInput {
	scrollDirection: ScrollDirection;
	isContextualHeaderVisible: boolean;
	isWithinHeroSection: boolean;
	scrollYPosition: number;
	anchorSectionTop: number;
	scrollTolerance: number;
}

interface HeaderState {
	readonly isVisible: boolean;
	readonly variant: HeaderVariant;
}

/**
 * Configuration options for controlling the contextual header visibility
 */
export interface UseContextualHeaderVisibilityControlOptions {
	/**
	 * Reference to the hero section element that appears at the top of the page.
	 * The contextual header will not be visible when the user is viewing this section.
	 */
	heroSectionRef?: MaybeRef<HTMLElement | null>;

	/**
	 * Reference to the section that determines when the contextual header should be hidden.
	 * When the user scrolls past this section, the header will be hidden again.
	 * If not provided, the first content section will be used as a fallback.
	 */
	anchorSection?: MaybeRef<HTMLElement | null>;

	/**
	 * Array of references to all section elements that should be tracked for visibility.
	 * These sections will be registered with the store and used to determine which
	 * section is currently active in the contextual header navigation.
	 * Each section should have an 'id' attribute and a 'data-section-name' attribute
	 * to properly display in the navigation.
	 * Order of elements in the array will determine the order of the sections in the navigation.
	 */
	sectionRefs: ComputedRef<
		(ComputedRef<any> | Readonly<ShallowRef<HTMLElement | null>>)[]
	>;
}

/**
 * Composable to control the visibility of the contextual header based on scroll position.
 * The header will be shown or hidden based on the user's scroll position relative to
 * the configured sections.
 *
 * @param {UseContextualHeaderVisibilityControlOptions} options - Configuration options for the header visibility
 */
export function useContextualHeaderVisibilityControl(
	options: UseContextualHeaderVisibilityControlOptions
) {
	const pageSectionsStore = usePageSectionsStore();
	const navigationStore = useNavigationStore();
	const { isContextualHeaderVisible } = storeToRefs(navigationStore);
	const isMenuBeingShown = inject(
		DependencyInjectionSymbols.isMenuBeingShown,
		ref(false)
	);
	const { y: scrollY } = useWindowScroll();
	const { scrollDirection } = useScrollDirection();

	const heroSectionElement = computed(() => {
		const el = options.heroSectionRef ? unref(options.heroSectionRef) : null;
		// A component's $el can be a Text/Comment node (fragment or v-if
		// root), which has no getBoundingClientRect
		if (!el || typeof el.getBoundingClientRect !== 'function') return null;
		return el;
	});

	const sectionRefs = computed(() => {
		return Array.from(pageSectionsStore.sectionRefs.values());
	});

	const isWithinHeroSection = ref(true);

	if (pageSectionsStore.sectionRefs.size !== 0) {
		pageSectionsStore.clearSectionRefs();
	}

	// Use requestAnimationFrame to batch layout reads and avoid forced reflow
	// SSR-safe: requestAnimationFrame only runs on client
	let heroCheckPending = false;

	watch([heroSectionElement, scrollY], () => {
		if (!heroSectionElement.value || heroCheckPending) return;

		// SSR guard: requestAnimationFrame and getBoundingClientRect only on client
		if (!import.meta.client) return;

		heroCheckPending = true;
		requestAnimationFrame(() => {
			heroCheckPending = false;
			if (heroSectionElement.value) {
				isWithinHeroSection.value =
					heroSectionElement.value.getBoundingClientRect().bottom - 200 > 0;
			}
		});
	});

	const firstSection = computed(() => {
		return (
			sectionRefs.value.find(
				el =>
					el.id.includes('overview') ||
					el.getAttribute('data-section-priority') === '1'
			) || null
		);
	});

	const updateContextualHeader = (tabId: string) => {
		if (pageSectionsStore.isAutomaticScrollingInteracting) return;
		navigationStore.setIsContextualHeaderVisible(true);
		pageSectionsStore.setActiveTabId(tabId);
	};

	const getElementTopPosition = (
		element: (typeof sectionRefs)['value'][number]
	): number => {
		if (!element) return 0;
		return element.getBoundingClientRect().top + window.scrollY;
	};

	const getMostVisibleElement = (elements: (typeof sectionRefs)['value']) => {
		if (elements.length === 0) return null;

		const windowHeight = window.innerHeight;

		// Batch all getBoundingClientRect calls together to minimize reflow
		const elementsWithRects = elements.map(element => ({
			element,
			rect: element.getBoundingClientRect()
		}));

		// Filter visible elements using cached rects
		const visibleElements = elementsWithRects.filter(
			({ rect }) => rect.top < windowHeight * 0.7 && rect.bottom > 0
		);

		if (visibleElements.length === 0) return null;

		// Find most visible using cached rects (no additional reflow)
		return visibleElements.reduce((prev, current) => {
			const prevVisibleHeight = Math.min(windowHeight, prev.rect.bottom);
			const currentVisibleHeight = Math.min(windowHeight, current.rect.bottom);

			return prevVisibleHeight > currentVisibleHeight ? prev : current;
		}).element;
	};

	const shouldHideHeader = (
		scrollPositionY: number,
		chosenSectionTop: number,
		offsetTolerance: number
	): boolean => {
		const chosenSectionThreshold = chosenSectionTop - offsetTolerance;

		return (
			isWithinHeroSection.value || scrollPositionY > chosenSectionThreshold
		);
	};

	const updateHeaderBasedOnScroll = () => {
		const mostVisibleElement = getMostVisibleElement(sectionRefs.value);

		if (mostVisibleElement) {
			updateContextualHeader(mostVisibleElement.id);
		} else {
			const prioritySection = firstSection.value;
			const secondSection = sectionRefs.value[1];

			if (prioritySection) {
				if (!secondSection) {
					updateContextualHeader(prioritySection.id);
					return;
				}

				const secondSectionTop = getElementTopPosition(secondSection);
				const currentScrollY = scrollY.value;

				if (currentScrollY < secondSectionTop) {
					updateContextualHeader(prioritySection.id);
				} else if (secondSection.id) {
					updateContextualHeader(secondSection.id);
				}
			}
		}
	};

	const calculateHeaderState = (input: HeaderStateInput): HeaderState => {
		const {
			scrollDirection,
			isContextualHeaderVisible,
			isWithinHeroSection,
			scrollYPosition
		} = input;

		let result: HeaderState;

		if (!isContextualHeaderVisible) {
			result = {
				isVisible: true,
				variant: scrollYPosition > 0 ? 'primary' : 'translucide'
			} as const;
		} else if (scrollDirection === 'up') {
			result = {
				isVisible: true,
				variant: 'primary'
			} as const;
		} else if (scrollDirection === 'down') {
			result = {
				isVisible: isWithinHeroSection,
				variant: 'primary'
			} as const;
		} else {
			result = {
				isVisible: true,
				variant: 'primary'
			} as const;
		}

		// Return calculated header state

		return result;
	};

	const calculateContextualHeaderVisibility = (
		scrollYPosition: number,
		anchorSectionTop: number,
		scrollTolerance: number,
		isWithinHeroSection: boolean
	): boolean => {
		return !shouldHideHeader(
			scrollYPosition,
			anchorSectionTop,
			scrollTolerance
		);
	};

	const getAnchorElement = (
		anchorSection: MaybeRef<HTMLElement | null> | undefined
	): HTMLElement | null => {
		if (anchorSection) {
			return unref(anchorSection);
		}
		const sections = sectionRefs.value;
		return (sections[sections.length - 1] as HTMLElement) || null;
	};

	const calculateElementPosition = (element: HTMLElement | null): number => {
		if (!element) return 0;

		// If it's from options.anchorSection, use offsetTop
		if (options.anchorSection && unref(options.anchorSection) === element) {
			return element.offsetTop;
		}

		// If it's from fallback sections, calculate bottom position
		const rect = element.getBoundingClientRect();
		return rect.top + window.scrollY + element.offsetHeight;
	};

	const createScrollContext = (
		scrollYPosition: number,
		anchorSectionTop: number,
		scrollTolerance: number
	) => ({
		scrollYPosition,
		anchorSectionTop,
		scrollTolerance,
		scrollDirection: scrollDirection.value,
		isWithinHeroSection: isWithinHeroSection.value
	});

	const shouldSetHeroAsActive = (isContextualHeaderVisible: boolean): boolean =>
		!isContextualHeaderVisible;

	const getHeroSectionId = (
		heroSectionRef?: MaybeRef<HTMLElement | null>
	): string | null => {
		if (!heroSectionRef) return null;
		const element = unref(heroSectionRef);
		return element?.id ?? null;
	};

	const applyHeaderStateChanges = (
		headerState: HeaderState,
		shouldSetHeroAsActive: boolean,
		heroSectionId: string | null
	): void => {
		navigationStore.setIsHeaderVisible(headerState.isVisible);
		navigationStore.setHeaderVariant(headerState.variant);

		if (shouldSetHeroAsActive && heroSectionId) {
			pageSectionsStore.setActiveTabId(heroSectionId);
		}
	};

	const resetStateToDefault = () => {
		// Reset all navigation states to defaults
		navigationStore.setIsContextualHeaderVisible(false);
		navigationStore.setIsHeaderVisible(true);
		navigationStore.setHeaderVariant('translucide');

		// Close all open menus
		navigationStore.closeMenus();
		navigationStore.setIsNavigationMenuOpen(false);
		navigationStore.setIsProfileNavigationMenuOpen(false);

		// Reset page sections states to defaults
		pageSectionsStore.setAutomaticScrollingInteracting(false);
		pageSectionsStore.setHasFeaturesByCategory(true);

		// Note: Animation states will be reset through reactive watch in contextualHeader

		// Set active tab to hero section if available
		const heroSectionId = getHeroSectionId(options.heroSectionRef);
		if (heroSectionId) {
			pageSectionsStore.setActiveTabId(heroSectionId);
		} else {
			// Fallback to overview as default
			pageSectionsStore.setActiveTabId('overview');
		}
	};

	const handleScroll = async (scrollYPosition: number) => {
		// Reset state to default when at the top of the page
		if (scrollYPosition === 0) {
			resetStateToDefault();
			return;
		}

		updateHeaderBasedOnScroll();

		const SCROLL_TOLERANCE = 200;
		const anchorElement = getAnchorElement(options.anchorSection);
		const anchorSectionTop = calculateElementPosition(anchorElement);

		const contextualHeaderVisibility = calculateContextualHeaderVisibility(
			scrollYPosition,
			anchorSectionTop,
			SCROLL_TOLERANCE,
			isWithinHeroSection.value
		);

		// Debug: Log what's happening
		// Calculate header visibility based on scroll context

		const scrollContext = createScrollContext(
			scrollYPosition,
			anchorSectionTop,
			SCROLL_TOLERANCE
		);

		const headerState = calculateHeaderState({
			...scrollContext,
			isContextualHeaderVisible: contextualHeaderVisibility
		});

		const heroSectionId = getHeroSectionId(options.heroSectionRef);
		const shouldActivateHero = shouldSetHeroAsActive(
			contextualHeaderVisibility
		);

		navigationStore.setIsContextualHeaderVisible(contextualHeaderVisibility);

		applyHeaderStateChanges(headerState, shouldActivateHero, heroSectionId);
	};

	watchThrottled(
		scrollY,
		() => {
			handleScroll(scrollY.value);
		},
		{
			throttle: 200
		}
	);

	watchPostEffect(() => {
		if (!import.meta.client) return;

		const registerSection = (ref: MaybeRef<HTMLElement | null> | undefined) => {
			if (!ref) return;
			const element = unref(ref);
			if (element && element.id) {
				pageSectionsStore.setSectionRef(element);
			}
		};

		pageSectionsStore.clearSectionRefs();

		if (options.heroSectionRef) {
			registerSection(options.heroSectionRef);
		}

		options.sectionRefs.value?.forEach(ref => {
			registerSection(ref);
		});
	});

	watch(isMenuBeingShown, newValue => {
		const scrollYPosition = scrollY.value;
		const variant: HeaderVariant =
			newValue || scrollYPosition > 0 ? 'primary' : 'translucide';
		navigationStore.setHeaderVariant(variant);
	});

	return {
		// All state is now managed in the navigation store
	};
}
